import type {
    AssetContainerModel,
    AudioAssetModel,
    DocumentAssetModel,
    FontAsset,
    IconAssetModel,
    LicenseEnum,
    SlideBackgroundModel,
    SourceEnum,
    TranscriptWord,
} from 'js/src/client';
import { AssetContainerTypes, AssetTypes } from 'js/src/client';
import type { UploadSearchContextType } from 'js/src/data/media/media-types';
import { SEARCH_CONTEXT } from 'js/src/data/media/media-types';
import type { StoreORMInterface } from 'js/src/data/store-orm';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { AugmentedFile } from 'js/src/libs/native-file-handler';

export type FileMetadata = {
    uuid?: string;
    source?: SourceEnum;
    source_id?: string;
    license?: LicenseEnum;
    description?: string;
    folderId?: number;
    isNewFolder?: boolean;
    folderName?: string;
    script?: string;
    videoId?: number;
    language?: string;
    ai_voice?: string;
    article_id?: number;
    document_id?: number;
    can_background_transcode?: boolean;
    from_clip_creator?: boolean;
    // metadata for script composer
    script_with_word_timings?: TranscriptWord[];
    // metadata for trimming,
    start_millis?: number;
    end_millis?: number;
    // metadata for ai media
    generation_uuid?: string;
    duration?: number;
};

export type FileWithMetadata = {
    file: AugmentedFile;
    metadata?: FileMetadata; // file-level metadata that overrides session-level metadata, useful for things like audioEnhancement
};

export type UploadProgressCallback = (progress: number, session_id: string) => void;
export type UploadSuccessCallback = (
    file_with_metadata: FileWithMetadata,
    newAsset: ImmutableMap<AssetContainerModel>,
    type: AssetContainerTypes,
    asyncOperationUuid: string,
) => void | Promise<void>;
export type UploadFailedCallback = (
    file_with_metadata: FileWithMetadata,
    message: string,
    e: Error,
) => void;
export type UploadMetadataUpdatedCallback = (
    file_with_metadata: FileWithMetadata,
    new_metadata: FileMetadata,
) => void;

export type UploadOptions = {
    file_list: FileWithMetadata[];
    asset_type: AssetContainerTypes;
    metadata?: FileMetadata; // session-level metadata that gets applied to all files, can be overwritten by file-level metadata
    progress_callback?: UploadProgressCallback;
    upload_succeeded_callback?: UploadSuccessCallback;
    upload_failed_callback?: UploadFailedCallback;
    metadata_updated_callback?: UploadMetadataUpdatedCallback;
    abort_controller?: AbortController;
    add_to_user_library?: boolean;
    min_width?: number;
    min_height?: number;
    max_width?: number;
    max_height?: number;
    only_images?: boolean;
    ignoreOtherUploads?: boolean;
    analyticsContext?: Record<string, string | number | boolean>;
    session_id?: string; // optional session ID to use for progress tracking
};

export type BackgroundAndIconAssetContainerTypes = [
    AssetContainerTypes.ICON,
    AssetContainerTypes.BACKGROUND,
];
// The combination of asset container types that Media Upload supports
export type UploadAssetContainerTypes =
    | AssetContainerTypes.BACKGROUND
    | AssetContainerTypes.AUDIO
    | AssetContainerTypes.ICON
    | BackgroundAndIconAssetContainerTypes;

export function isBackgroundAndIconAssetContainerType(
    value: UploadAssetContainerTypes,
): value is BackgroundAndIconAssetContainerTypes {
    if (!Array.isArray(value) || value.length !== 2) {
        return false;
    }
    return (
        value.includes(AssetContainerTypes.BACKGROUND) && value.includes(AssetContainerTypes.ICON)
    );
}

/* ACTION TYPES */
export const UPLOAD_ACTIONS = {
    ADD_FILES: 'ADD_FILES',
    STORE_UPLOAD_PROGRESS: 'STORE_UPLOAD_PROGRESS',
    STORE_SUCCESSFUL_UPLOAD: 'STORE_SUCCESSFUL_UPLOAD',
    UPDATE_UPLOADED_ASSET_MEDIA_TAGS: 'UPDATE_UPLOADED_ASSET_MEDIA_TAGS',
    STORE_FAILED_UPLOAD: 'STORE_FAILED_UPLOAD',
    CLEAR_FAILED_UPLOAD_MESSAGES: 'CLEAR_FAILED_UPLOAD_MESSAGES',
    USER_CANCEL_FILE: 'USER_CANCEL_FILE',
    CANCEL_ALL_UPLOADS: 'CANCEL_ALL_UPLOADS',
    START_UPLOAD_EXPERIENCE: 'START_UPLOAD_EXPERIENCE',
    END_UPLOAD_EXPERIENCE: 'END_UPLOAD_EXPERIENCE',
    SET_UPLOAD_FOLDER: 'SET_UPLOAD_FOLDER',
    FINALIZE_ATTRIBUTION_DATA: 'FINALIZE_ATTRIBUTION_DATA',
    UPDATE_ATTRIBUTION_DATA: 'UPDATE_ATTRIBUTION_DATA',
    UPDATE_UPLOAD_MEDIA_TAGS: 'UPDATE_UPLOAD_MEDIA_TAGS',
    MARK_FILE_AS_DELETING: 'MARK_FILE_AS_DELETING',
    MARK_FILE_AS_FINALIZING: 'MARK_FILE_AS_FINALIZING',
    MARK_FILE_AS_DELETED: 'MARK_FILE_AS_DELETED',
    STORE_COMPLETED_FILE: 'STORE_COMPLETED_FILE',
    STORE_UPDATED_FILE_METADATA: 'STORE_UPDATED_FILE_METADATA',
    MARK_FILE_AS_DELETION_FAILED: 'MARK_FILE_AS_DELETION_FAILED',
    REMOVE_UPLOAD: 'REMOVE_UPLOAD',
    CLEAR_UPLOADS: 'CLEAR_UPLOADS',
    CREATE_UPLOAD_SESSION: 'CREATE_UPLOAD_SESSION',
    DELETE_UPLOAD_SESSION: 'DELETE_UPLOAD_SESSION',
};

export enum FILE_STATUS {
    LOADING = 'loading',
    FINALIZING = 'finalizing',
    DELETING = 'deleting',
    FAILED = 'failed',
    VALIDATED = 'validated',
    COMPLETED = 'completed',
    DELETED = 'deleted',
    DELETE_FAILED = 'delete_failed',
}

export type UploadOptionsWithoutFileList = Omit<UploadOptions, 'file_list'>;
export type FileUpload = {
    file_obj: AugmentedFile;
    file_metadata: FileMetadata;
    status: FILE_STATUS;
    user_canceled: boolean;
    fail_message: string;
    tags: Array<string>;
    upload_opts: UploadOptionsWithoutFileList;
    uploaded_asset: AssetContainerModel | null;
    // To track the upload operation uuid when multiple files are uploaded at once
    asyncOperationUuid?: string;
    id: string;
};

export type UploadedAudioAsset = {
    type: AssetContainerTypes.AUDIO;
    asset: AudioAssetModel;
};
export type UploadedIconAsset = {
    type: AssetContainerTypes.ICON;
    asset: IconAssetModel;
};
export type UploadedBackgroundAsset = {
    type: AssetContainerTypes.BACKGROUND;
    asset: SlideBackgroundModel;
};
export type UploadedAsset = UploadedBackgroundAsset | UploadedAudioAsset | UploadedIconAsset;
export type UploadableAsset =
    | AudioAssetModel
    | SlideBackgroundModel
    | FontAsset
    | IconAssetModel
    | DocumentAssetModel;
export type MyImmutableUploadedAsset = ImmutableMap<
    UploadedBackgroundAsset | UploadedAudioAsset | UploadedIconAsset
>;

export const uploadedAssetIsBackgroundAsset = (
    uploaded_asset: MyImmutableUploadedAsset,
): uploaded_asset is ImmutableMap<UploadedBackgroundAsset> => {
    return uploaded_asset.get('type') === AssetContainerTypes.BACKGROUND;
};

export const uploadedAssetIsAudioAsset = (
    uploaded_asset: MyImmutableUploadedAsset,
): uploaded_asset is ImmutableMap<UploadedAudioAsset> => {
    return uploaded_asset.get('type') === AssetContainerTypes.AUDIO;
};

export const uploadedAssetIsIconAsset = (
    uploaded_asset: MyImmutableUploadedAsset,
): uploaded_asset is ImmutableMap<UploadedIconAsset> => {
    return uploaded_asset.get('type') === AssetContainerTypes.ICON;
};

export const RECORDING_FILENAME_PREFIX = 'recording'; // should only contain alphanumeric characters
export enum UploadingContext {
    VoiceRecording = 'voice-recording',
    VideoRecording = 'video-recording',
    ScreenRecording = 'screen-recording',
    VoiceFileUpload = 'voice-file-upload',
    VideoFileUpload = 'video-file-upload',
}

export type UploadSession = {
    files: StoreORMInterface<FileUpload, string>;
    files_can_be_edited: boolean;
    folderId: number | null;
    attribution: string;
    media_tags: Immutable.List<string>;
    license: LicenseEnum;
};

export const MINIMUM_FILE_UPLOAD_DIMS = {
    width: 720,
    height: 720,
};

export type BackgroundAssetContainerModel = AssetContainerModel & {
    type: AssetContainerTypes.BACKGROUND;
    background: SlideBackgroundModel;
};
export const assetContainerModelIsBackground = (
    assetContainerModel: AssetContainerModel,
): assetContainerModel is BackgroundAssetContainerModel => {
    return assetContainerModel.type === AssetContainerTypes.BACKGROUND;
};
export const immutableAssetContainerModelIsBackground = (
    assetContainerModel: ImmutableMap<AssetContainerModel>,
): assetContainerModel is ImmutableMap<BackgroundAssetContainerModel> => {
    return assetContainerModel.get('type') === AssetContainerTypes.BACKGROUND;
};

export type IconAssetContainerModel = AssetContainerModel & {
    type: AssetContainerTypes.ICON;
    icon: IconAssetModel;
};
export const assetContainerModelIsIcon = (
    assetContainerModel: AssetContainerModel,
): assetContainerModel is IconAssetContainerModel => {
    return assetContainerModel.type === AssetContainerTypes.ICON;
};
export const immutableAssetContainerModelIsIcon = (
    assetContainerModel: ImmutableMap<AssetContainerModel>,
): assetContainerModel is ImmutableMap<IconAssetContainerModel> => {
    return assetContainerModel.get('type') === AssetContainerTypes.ICON;
};

export type AudioAssetContainerModel = AssetContainerModel & {
    type: AssetContainerTypes.AUDIO;
    audio: AudioAssetModel;
};
export const assetContainerModelIsAudio = (
    assetContainerModel: AssetContainerModel,
): assetContainerModel is AudioAssetContainerModel => {
    return assetContainerModel.type === AssetContainerTypes.AUDIO;
};
export const immutableAssetContainerModelIsAudio = (
    assetContainerModel: ImmutableMap<AssetContainerModel>,
): assetContainerModel is ImmutableMap<AudioAssetContainerModel> => {
    return assetContainerModel.get('type') === AssetContainerTypes.AUDIO;
};

export type DocumentAssetContainerModel = AssetContainerModel & {
    type: AssetContainerTypes.DOCUMENT;
    document: DocumentAssetModel;
};
export const assetContainerModelIsDocument = (
    assetContainerModel: AssetContainerModel,
): assetContainerModel is DocumentAssetContainerModel => {
    return assetContainerModel.type === AssetContainerTypes.DOCUMENT;
};
export const immutableAssetContainerModelIsDocument = (
    assetContainerModel: ImmutableMap<AssetContainerModel>,
): assetContainerModel is ImmutableMap<DocumentAssetContainerModel> => {
    return assetContainerModel.get('type') === AssetContainerTypes.DOCUMENT;
};

export type FontAssetContainerModel = AssetContainerModel & {
    type: AssetContainerTypes.FONT;
    font: FontAsset;
};
export const assetContainerModelIsFont = (
    assetContainerModel: AssetContainerModel,
): assetContainerModel is FontAssetContainerModel => {
    return assetContainerModel.type === AssetContainerTypes.FONT;
};
export const immutableAssetContainerModelIsFont = (
    assetContainerModel: ImmutableMap<AssetContainerModel>,
): assetContainerModel is ImmutableMap<FontAssetContainerModel> => {
    return assetContainerModel.get('type') === AssetContainerTypes.FONT;
};

/**
 * Util to look up props shared between media upload button and dropzone when opening the modal.
 * Converts a search context to the appropriate asset container types and constraints.
 *
 * @param searchContext - The upload search context (e.g., UPLOADED_ELEMENTS, UPLOADED_VISUALS)
 * @returns Object with assetContainerTypes and optional constrainToBackgroundAssetType
 */
export function getMediaUploadModalAssetPropsFromSearchContext(
    searchContext: UploadSearchContextType,
): {
    assetContainerTypes: UploadAssetContainerTypes;
    constrainToBackgroundAssetType: AssetTypes.IMAGE | undefined;
} {
    const assetContainerTypesToSearchContext: Record<
        UploadSearchContextType,
        UploadAssetContainerTypes
    > = {
        [SEARCH_CONTEXT.UPLOADED_VISUALS]: AssetContainerTypes.BACKGROUND,
        [SEARCH_CONTEXT.UPLOADED_AUDIO]: AssetContainerTypes.AUDIO,
        [SEARCH_CONTEXT.UPLOADED_ELEMENTS]: [
            AssetContainerTypes.ICON,
            AssetContainerTypes.BACKGROUND,
        ],
        [SEARCH_CONTEXT.UPLOADED_VOICEOVERS]: AssetContainerTypes.AUDIO,
    };
    const assetContainerTypes = assetContainerTypesToSearchContext[searchContext];
    const constrainToBackgroundAssetType =
        searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS ? AssetTypes.IMAGE : undefined;
    return {
        assetContainerTypes,
        constrainToBackgroundAssetType,
    };
}

/**
 * Converts UploadAssetContainerTypes to AssetTypes[] for use in UI and error messages.
 * This ensures consistent file type messaging across the upload flow.
 *
 * @param assetContainerTypes - The container type(s) being uploaded
 * @param constrainToBackgroundAssetType - Optional constraint to only allow images (used in elements tab)
 * @returns Array of AssetTypes that can be passed to getUploadableFormatsMessage()
 */
export function convertUploadAssetContainerTypesToAssetTypes(
    assetContainerTypes: UploadAssetContainerTypes,
    constrainToBackgroundAssetType?: AssetTypes,
): AssetTypes[] {
    if (isBackgroundAndIconAssetContainerType(assetContainerTypes)) {
        if (constrainToBackgroundAssetType) {
            return [AssetTypes.ICON, constrainToBackgroundAssetType];
        }
        return [AssetTypes.VIDEO, AssetTypes.IMAGE, AssetTypes.ICON];
    }
    switch (assetContainerTypes) {
        case AssetContainerTypes.BACKGROUND:
            if (constrainToBackgroundAssetType) {
                return [constrainToBackgroundAssetType];
            }
            return [AssetTypes.VIDEO, AssetTypes.IMAGE];
        case AssetContainerTypes.AUDIO:
            return [AssetTypes.AUDIO];
        case AssetContainerTypes.ICON:
            return [AssetTypes.ICON];
        default:
            throw Error('Unable to determine AssetTypes from ' + String(assetContainerTypes));
    }
}
